import {GitHubAvatar} from '@github-ui/github-avatar'
import {useQuery} from '@github-ui/react-query'
import {testIdProps} from '@github-ui/test-id-props'
import {CheckIcon, DashIcon, OrganizationIcon, PersonIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Checkbox, Flash, FormControl, Spinner, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useEffect, useMemo, useRef, useState} from 'react'

import {copyProjectOwnersQueryOptions} from '../../queries/copy-project-owners'
import styles from './CopyProjectDialog.module.css'
import {navigateToHtmlUrl} from './navigate-to-html-url'
import type {CopyProjectOwner, Project} from './types'
import {useCopyProject} from './use-copy-project'

export type CopyProjectDialogMode = 'copy' | 'template'

interface CopyProjectDialogProps {
  project: Project
  mode: CopyProjectDialogMode
  onClose: () => void
}

const COPY_TITLE_PREFIX = '[COPY]'
const TEMPLATE_TITLE_PREFIX = '[TEMPLATE]'

export function CopyProjectDialog({project, mode, onClose}: CopyProjectDialogProps) {
  const isTemplateMode = mode === 'template'
  const dialogTitle = isTemplateMode ? 'Copy as template' : 'Make a copy'
  const submitLabel = isTemplateMode ? 'Copy as template' : 'Copy project'

  const ownersQuery = useQuery(copyProjectOwnersQueryOptions({projectId: project.id, copyAsTemplate: isTemplateMode}))
  const owners = ownersQuery.data?.owners ?? EMPTY_OWNERS

  const [selectedOwnerLogin, setSelectedOwnerLogin] = useState<string | null>(null)
  if (selectedOwnerLogin === null && owners.length > 0) {
    setSelectedOwnerLogin(owners[0]!.login)
  }
  const selectedOwner = useMemo(
    () => owners.find(o => o.login === selectedOwnerLogin) ?? null,
    [owners, selectedOwnerLogin],
  )

  const [title, setTitle] = useState(() => buildDefaultTitle(project.title, mode))
  const [includeDraftIssues, setIncludeDraftIssues] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const trimmedTitle = title.trim()
  const titleIsValid = trimmedTitle.length > 0

  const copyMutation = useCopyProject({
    sourceOwnerLogin: project.owner,
    // Per-row, not page-level: a transferred-repo edge case can leave the
    // project's owner and the repo's owner pointing at different accounts, so
    // the URL segment must match the project's owner specifically.
    sourceOwnerIsOrganization: project.projectOwnerIsOrganization,
    sourceProjectNumber: project.number,
  })

  const submitInactive = !selectedOwner || !titleIsValid || copyMutation.isPending

  const abortControllerRef = useRef<AbortController | null>(null)

  const handleSubmit = async () => {
    // Footer buttons can be triggered by Enter from a focused text field even
    // when visually inactive, so re-check here to avoid duplicate submits.
    if (submitInactive) {
      return
    }
    // Clear any prior error state so the retry shows a fresh "Copying…" label
    // instead of carrying the previous failure into the new attempt.
    copyMutation.reset()
    setErrorMessage(null)
    const controller = new AbortController()
    abortControllerRef.current = controller
    try {
      const data = await copyMutation.mutateAsync({
        targetOwner: selectedOwner,
        title: trimmedTitle,
        mode,
        includeDraftIssues: !isTemplateMode && includeDraftIssues,
        signal: controller.signal,
      })
      // Close before navigating so the source page's overlay is gone by the
      // time the browser starts unloading.
      onClose()
      navigateToHtmlUrl(data.htmlUrl)
    } catch (error) {
      if (controller.signal.aborted) {
        return
      }
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
    }
  }

  const handleClose = () => {
    abortControllerRef.current?.abort()
    onClose()
  }

  // Focus and select the title field on mount so the user's first edit lands
  // there. The owner picker auto-selects, so it doesn't need initial focus.
  const titleInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    titleInputRef.current?.select()
  }, [])

  return (
    <Dialog
      title={dialogTitle}
      width="large"
      onClose={handleClose}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: handleClose},
        {
          buttonType: 'primary',
          content: copyMutation.isPending ? 'Copying…' : submitLabel,
          onClick: handleSubmit,
          inactive: submitInactive,
          autoFocus: false,
        },
      ]}
    >
      <div {...testIdProps(isTemplateMode ? 'copy-project-as-template-dialog' : 'copy-project-dialog')}>
        <p className={styles.description}>
          {isTemplateMode
            ? 'Copy this project into a template that can be used when creating new projects.'
            : 'Make a copy of this project that can be used as a starting point for another project.'}
        </p>

        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles --
          Safari/VoiceOver doesn't announce `<ul>` elements as lists when
          `list-style: none` is set in CSS (WCAG 1.3.1). Adding `role="list"`
          restores the list semantics so AT users hear "list, N items".
         */}
        <ul className={styles.infoList} role="list">
          <CopyInfoListItem icon={<CheckIcon aria-label="Included" />} tone="success">
            Views, custom fields, insights, and workflows will be copied.
          </CopyInfoListItem>
          <CopyInfoListItem icon={<DashIcon aria-label="Not included" />} tone="muted">
            Issues and pull requests will not be copied.
          </CopyInfoListItem>
          {!isTemplateMode && (
            <li className={styles.infoListItem}>
              {/*
                FormControl auto-generates the htmlFor/id association between
                Checkbox and FormControl.Label — don't break the wrapping.
              */}
              <FormControl>
                <Checkbox
                  // Override Safari's default skipping of checkboxes in the Tab order (macOS Safari).
                  tabIndex={0}
                  checked={includeDraftIssues}
                  onChange={e => setIncludeDraftIssues(e.target.checked)}
                  {...testIdProps('copy-project-include-draft-issues')}
                />
                <FormControl.Label className={styles.draftIssuesLabel}>
                  Draft issues will be copied if selected
                </FormControl.Label>
              </FormControl>
            </li>
          )}
        </ul>

        <FormControl required className={styles.field}>
          <FormControl.Label>Owner</FormControl.Label>
          <ActionMenu>
            {/*
              `aria-required="true"` is set directly on the button because
              `FormControl required` doesn't propagate to a non-native control
              like `ActionMenu.Button`.
            */}
            <ActionMenu.Button
              aria-required="true"
              loading={ownersQuery.isFetching && owners.length === 0}
              disabled={owners.length === 0 && !ownersQuery.isFetching}
              leadingVisual={() =>
                selectedOwner ? (
                  <GitHubAvatar src={selectedOwner.avatarUrl} alt="" size={20} square={selectedOwner.isOrganization} />
                ) : (
                  <span className={styles.ownerAvatarPlaceholder} aria-hidden="true" />
                )
              }
              {...testIdProps('copy-project-owner-button')}
            >
              {selectedOwner ? selectedOwner.login : 'Select an owner'}
            </ActionMenu.Button>
            <ActionMenu.Overlay align="start" width="medium">
              <ActionList selectionVariant="single" aria-label="Owner for the new project">
                {ownersQuery.isFetching && owners.length === 0 ? (
                  <ActionList.Item disabled>
                    <ActionList.LeadingVisual>
                      <Spinner size="small" />
                    </ActionList.LeadingVisual>
                    Loading owners…
                  </ActionList.Item>
                ) : owners.length === 0 ? (
                  <ActionList.Item disabled>No owners available</ActionList.Item>
                ) : (
                  owners.map(owner => (
                    <ActionList.Item
                      key={owner.login}
                      selected={owner.login === selectedOwnerLogin}
                      onSelect={() => setSelectedOwnerLogin(owner.login)}
                    >
                      <ActionList.LeadingVisual>
                        <GitHubAvatar src={owner.avatarUrl} alt="" size={20} square={owner.isOrganization} />
                      </ActionList.LeadingVisual>
                      {owner.login}
                      <ActionList.TrailingVisual>
                        {owner.isOrganization ? (
                          <OrganizationIcon aria-hidden="true" />
                        ) : (
                          <PersonIcon aria-hidden="true" />
                        )}
                      </ActionList.TrailingVisual>
                    </ActionList.Item>
                  ))
                )}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
          {ownersQuery.isError && (
            <FormControl.Validation variant="error">Couldn&apos;t load owners. Try again later.</FormControl.Validation>
          )}
        </FormControl>

        <FormControl required className={styles.field}>
          <FormControl.Label>New project name</FormControl.Label>
          <TextInput
            ref={titleInputRef}
            block
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Name"
            {...testIdProps('copy-project-title-input')}
          />
        </FormControl>

        {errorMessage && (
          <Flash variant="danger" role="alert" {...testIdProps('copy-project-error')}>
            {errorMessage}
          </Flash>
        )}
      </div>
    </Dialog>
  )
}

const EMPTY_OWNERS: CopyProjectOwner[] = []

function buildDefaultTitle(sourceTitle: string, mode: CopyProjectDialogMode): string {
  const prefix = mode === 'template' ? TEMPLATE_TITLE_PREFIX : COPY_TITLE_PREFIX
  return `${prefix} ${sourceTitle}`
}

interface CopyInfoListItemProps {
  icon: React.ReactNode
  tone: 'success' | 'muted'
  children: React.ReactNode
}

function CopyInfoListItem({icon, tone, children}: CopyInfoListItemProps) {
  const iconClassName = clsx(styles.infoIcon, tone === 'success' ? styles.infoIconSuccess : styles.infoIconMuted)
  return (
    <li className={styles.infoListItem}>
      <span className={iconClassName}>{icon}</span>
      <span>{children}</span>
    </li>
  )
}
